import classNames from "classnames";
import React from "react";

// styles
import styles from "./styles.module.scss";
import { useTranslation } from "react-i18next";

interface Props {
  pagesCount: number;
  page: number;
  length: number;
  pageSize: number;
  countName: string;
  handlePaginationChange: (value: number) => void;
}

const Pagination: React.FC<Props> = ({
  length,
  page,
  pagesCount,
  pageSize,
  handlePaginationChange,
  countName,
}: Props): JSX.Element => {
  const { t } = useTranslation();

  if (pagesCount < 2) return <></>;

  const getVisiblePages = (): (number | "ellipsis")[] => {
    const pagesSet = new Set<number>();
    // first 3 pages
    for (let p = 1; p <= Math.min(3, pagesCount); p++) pagesSet.add(p);
    // last 3 pages
    for (let p = Math.max(1, pagesCount - 2); p <= pagesCount; p++)
      pagesSet.add(p);
    // ensure current page is visible
    if (page >= 1 && page <= pagesCount) pagesSet.add(page);

    const sorted = Array.from(pagesSet).sort((a, b) => a - b);
    const result: (number | "ellipsis")[] = [];
    for (let i = 0; i < sorted.length; i++) {
      const current = sorted[i];
      const prev = sorted[i - 1];
      if (i > 0 && prev !== undefined && current - prev > 1) {
        result.push("ellipsis");
      }
      result.push(current);
    }
    return result;
  };

  const visible = getVisiblePages();

  return (
    <div className={length ? styles.container : styles.hide}>
      <p>
        {t("lbl_total_count_for_pagination", {
          val1: (page - 1) * pageSize + 1,
          val2: page * pageSize >= length ? length : page * pageSize,
          val3: length,
          val4: countName,
        })}
      </p>
      <div dir="rtl" className={styles.list}>
        <div
          onClick={() => {
            page !== 1 && handlePaginationChange(page - 1);
          }}
          className={classNames(styles.prev, page == 1 && styles.disabled)}>
          <img src="/assets/images/svg/arrowDark.svg" />
        </div>

        {visible.map((item, idx) => {
          if (item === "ellipsis") {
            return (
              <li
                key={`ellipsis-${idx}`}
                className={classNames(styles.dots_container)}>
                ...
              </li>
            );
          }
          const p = item as number;
          return (
            <li
              key={p}
              onClick={() => handlePaginationChange(p)}
              className={classNames(
                styles.page_item,
                page === p && styles.page_selected
              )}>
              {p}
            </li>
          );
        })}

        <div
          onClick={() => {
            page !== pagesCount && handlePaginationChange(page + 1);
          }}
          className={classNames(
            styles.next,
            page == pagesCount && styles.disabled
          )}>
          <img
            className={styles.rotate}
            src="/assets/images/svg/arrowDark.svg"
          />
        </div>
      </div>
    </div>
  );
};

export default Pagination;
